import React, { useState, useContext } from 'react';
import { LSEMessage } from '../types/Lse';
import { preProdLogger } from '../utils/log.utils';
import { useApi } from './api.context';
import { RoutePath } from '@/src/RoutePath';
import { Redirect } from 'react-router-dom';
import { NoMatch } from '@/src/components/common/NoMatch/NoMatch';
import { noop } from 'lodash';
import { config } from '@/src/config/app-config';
import { AuthRole } from '@/src/constants/shared';

export interface LSEContextValue {
  currentLSE: LSEMessage | undefined;
  globalNotifications: LSEMessage[] | undefined;
  loadCurrentLSE: () => void;
  loadGlobalNotifications: () => Promise<void>;
  createLSE: (lseMessage: LSEMessage) => void;
  createGlobalNotification: (
    lseMessage: LSEMessage,
    notificationType?: 'lse' | 'global-notification',
    audience?: AuthRole[]
  ) => Promise<void>;
  updateLSE: (
    lseMessage: LSEMessage,
    notificationId?: string,
    notificationType?: 'lse' | 'global-notification'
  ) => Promise<void>;
  deactivateLSE: (id?: string) => Promise<void>;
  loading: boolean;
}

const LSEContext = React.createContext<LSEContextValue>({
  currentLSE: undefined,
  globalNotifications: undefined,
  loadCurrentLSE: () => noop,
  loadGlobalNotifications: () => Promise.resolve(),
  createLSE: (_lseMessage: LSEMessage) => noop,
  createGlobalNotification: () => Promise.resolve(),
  updateLSE: (_lseMessage: LSEMessage, _notificationId = undefined, _notificationType = 'lse') => Promise.resolve(),
  deactivateLSE: () => Promise.resolve(),
  loading: false,
});

const LSEProvider: React.FC = ({ children }) => {
  const [currentLSE, setCurrentLSE] = useState<LSEMessage | undefined>(undefined);
  const [globalNotifications, setGlobalNotifications] = useState<LSEMessage[] | undefined>(undefined);
  const { lseApi } = useApi();
  const [caughtError, setCaughtError] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadCurrentLSE = () => {
    setLoading(true);
    lseApi
      .getLSEMessage()
      .then((res) => {
        setLoading(false);
        if (res) {
          setCurrentLSE(res);
        }
      })
      .catch((err) => {
        setLoading(false);
        setCaughtError(true);
        preProdLogger('Error retrieving LSE Message', err.message);
      });
  };

  const loadGlobalNotifications = () => {
    setLoading(true);
    return lseApi
      .getGlobalNotifications()
      .then((res) => {
        if (res) {
          const globalNotifications = res.filter((item) => item.notificationType === 'global-notification');
          const lse = res.find((item) => item?.notificationType === 'lse' || !item?.notificationType);
          setGlobalNotifications(globalNotifications);
          setCurrentLSE(lse);
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Error fetching global notifications', err);
        setCaughtError(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const createLSE = (lseMessage: LSEMessage) => {
    setLoading(true);
    if (lseMessage.text) {
      lseApi
        .createLSE(lseMessage)
        .then(() => {
          setLoading(false);
          loadCurrentLSE();
        })
        .catch((err) => {
          setLoading(false);
          preProdLogger('Error creating LSE', err.message);
        });
    }
  };

  const createGlobalNotification = (
    message: LSEMessage,
    notification?: 'lse' | 'global-notification',
    audience?: AuthRole[]
  ) => {
    setLoading(true);
    return lseApi
      .createGlobalNotification(message, notification || 'lse', audience || [AuthRole.GUEST_USER])
      .then(() => {
        return loadGlobalNotifications();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const updateLSE = (
    lseMessage: LSEMessage,
    notificationId?: string,
    notificationType: 'lse' | 'global-notification' = 'lse'
  ) => {
    setLoading(true);
    return lseApi
      .updateLSE(lseMessage, notificationId, notificationType)
      .then(() => {
        setLoading(false);
        if (config.ff.enableGlobalNotifications) {
          return loadGlobalNotifications();
        } else {
          setCurrentLSE(undefined);
          loadCurrentLSE();
        }
      })
      .catch((err) => {
        setLoading(false);
        throw err;
      });
  };

  const deactivateLSE = (id?: string) => {
    if (currentLSE || !!globalNotifications?.length) {
      setLoading(true);
      return lseApi
        .deactivateLSE(id)
        .then(() => {
          setLoading(false);
          if (id) {
            return loadGlobalNotifications();
          } else {
            setCurrentLSE(undefined);
          }
        })
        .catch((err) => {
          setLoading(false);
          throw err;
        });
    }
    return Promise.resolve();
  };

  const data: LSEContextValue = {
    currentLSE,
    loadCurrentLSE,
    loadGlobalNotifications,
    globalNotifications,
    createLSE,
    createGlobalNotification,
    updateLSE,
    deactivateLSE,
    loading,
  };

  if (caughtError) {
    <Redirect to={{ pathname: RoutePath.JAM_500 }} />;
    return <NoMatch path={RoutePath.JAM_500} />;
  }
  return <LSEContext.Provider value={data}>{children}</LSEContext.Provider>;
};

const useLSE = () => {
  const context = useContext(LSEContext);
  if (context === undefined) {
    throw new Error('useLSEs can only be used inside LSEProvider');
  }
  return context;
};

export { LSEProvider, useLSE };
